import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftRight,
  Bomb,
  CircleOff,
  Flame,
  Map,
  Medal,
  Stars,
  TrendingUp,
  Trophy,
  Wand,
} from 'lucide-react';
import React from 'react';

import { formatMapName } from '@/utils/format';

import { fetchBestMap } from '../api/stats/getBestMap';
import { fetchBombplantsPerGame } from '../api/stats/getBombplants';
import { fetchDefuses } from '../api/stats/getDefuses';
import { fetchFlashbangAssistsRate } from '../api/stats/getFlashbangAssists';
import { fetchMapsPlayed } from '../api/stats/getMapsPlayed';
import { fetchTopFragger } from '../api/stats/getTopFragger';
import { fetchTradeKillsPerGame } from '../api/stats/getTradeKills';
import { fetchUtilityDamage } from '../api/stats/getUtilityDamage';
import { fetchWinRate } from '../api/stats/getWinRate';
import { fetchWinStreak } from '../api/stats/getWinStreak';

interface TeamStatsProps {
  teamId: string;
}
const TeamStats: React.FC<TeamStatsProps> = ({ teamId }) => {
  const { data: mapsPlayed } = useQuery({
    queryKey: ['mapsPlayed', teamId],
    queryFn: () => fetchMapsPlayed(teamId),
  });
  const { data: bestMap } = useQuery({
    queryKey: ['bestMap', teamId],
    queryFn: () => fetchBestMap(teamId),
  });
  const { data: winRate } = useQuery({
    queryKey: ['winRate', teamId],
    queryFn: () => fetchWinRate(teamId),
  });
  const { data: topFragger } = useQuery({
    queryKey: ['topFragger', teamId],
    queryFn: () => fetchTopFragger(teamId),
  });
  const { data: winStreak } = useQuery({
    queryKey: ['winStreak', teamId],
    queryFn: () => fetchWinStreak(teamId),
  });
  const { data: bombPlants } = useQuery({
    queryKey: ['bombPlants', teamId],
    queryFn: () => fetchBombplantsPerGame(teamId),
  });
  const { data: tradeKills } = useQuery({
    queryKey: ['tradeKills', teamId],
    queryFn: () => fetchTradeKillsPerGame(teamId),
  });
  const { data: utilityDamage } = useQuery({
    queryKey: ['utilityDamage', teamId],
    queryFn: () => fetchUtilityDamage(teamId),
  });
  const { data: defuses } = useQuery({
    queryKey: ['defuses', teamId],
    queryFn: () => fetchDefuses(teamId),
  });
  const { data: flashbang } = useQuery({
    queryKey: ['flashbang', teamId],
    queryFn: () => fetchFlashbangAssistsRate(teamId),
  });

  return (
    <div>
      <div className="mb-3 text-lg font-semibold">Statistics</div>
      <div className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-4 lg:grid-cols-5">
        <div className="flex h-48 flex-col items-center justify-center gap-y-2.5 rounded-md border border-border px-6 py-4 text-center text-xs">
          <h3 className="grid h-12 place-items-center text-4xl font-semibold lg:text-5xl">
            {mapsPlayed ? mapsPlayed : 0}
          </h3>
          <p className="font-semibold uppercase text-muted-foreground">
            MAPS PLAYERD
          </p>
          <Map className="text-primary" />
        </div>
        <div className="flex h-48 flex-col items-center justify-center gap-y-2.5 rounded-md border border-border px-6 py-4 text-center text-xs">
          <h3 className="grid h-12 place-items-center text-xl font-semibold lg:text-2xl">
            {bestMap ? (
              formatMapName(bestMap)
            ) : (
              <span className="text-4xl lg:text-5xl">-</span>
            )}
          </h3>
          <p className="font-semibold uppercase text-muted-foreground">
            BEST MAP
          </p>
          <Stars className="text-primary" />
        </div>
        <div className="flex h-48 flex-col items-center justify-center gap-y-2.5 rounded-md border border-border px-6 py-4 text-center text-xs">
          <h3 className="grid h-12 place-items-center text-4xl font-semibold lg:text-5xl">
            {winRate ? winRate + '%' : '-'}
          </h3>
          <p className="font-semibold uppercase text-muted-foreground">
            WINRATE
          </p>
          <Trophy className="text-primary" />
        </div>
        <div className="flex h-48 flex-col items-center justify-center gap-y-2.5 rounded-md border border-border px-6 py-4 text-center text-xs">
          <h3 className="grid h-12 place-items-center text-xl font-semibold lg:text-2xl">
            {topFragger ? (
              topFragger
            ) : (
              <span className="text-4xl lg:text-5xl">-</span>
            )}
          </h3>
          <p className="font-semibold uppercase text-muted-foreground">
            TOP FRAGGER
          </p>
          <Medal size={16} className="text-primary" />
        </div>
        <div className="flex h-48 flex-col items-center justify-center gap-y-2.5 rounded-md border border-border px-6 py-4 text-center text-xs">
          <h3 className="grid h-12 place-items-center text-4xl font-semibold lg:text-5xl">
            {winStreak ? mapsPlayed : 0}
          </h3>
          <p className="font-semibold uppercase text-muted-foreground">
            LONGEST WINSTREAK
          </p>
          <TrendingUp size={16} className="text-primary" />
        </div>
        <div className="flex h-48 flex-col items-center justify-center gap-y-2.5 rounded-md border border-border px-6 py-4 text-center text-xs">
          <h3 className="grid h-12 place-items-center text-4xl font-semibold lg:text-5xl">
            {flashbang ? flashbang : '-'}
          </h3>
          <p className="font-semibold uppercase text-muted-foreground">
            FLASHBANG ASSIST RATE
          </p>
          <Wand size={16} className="text-primary" />
        </div>
        <div className="flex h-48 flex-col items-center justify-center gap-y-2.5 rounded-md border border-border px-6 py-4 text-center text-xs">
          <h3 className="grid h-12 place-items-center text-4xl font-semibold lg:text-5xl">
            {tradeKills ? tradeKills : '-'}
          </h3>
          <p className="font-semibold uppercase text-muted-foreground">
            TRADE KILLS PER GAME
          </p>
          <ArrowLeftRight size={16} className="text-primary" />
        </div>
        <div className="flex h-48 flex-col items-center justify-center gap-y-2.5 rounded-md border border-border px-6 py-4 text-center text-xs">
          <h3 className="grid h-12 place-items-center text-4xl font-semibold lg:text-5xl">
            {bombPlants ? bombPlants : '-'}
          </h3>
          <p className="font-semibold uppercase text-muted-foreground">
            BOMB PLANTS PER GAME
          </p>
          <Bomb size={16} className="text-primary" />
        </div>
        <div className="flex h-48 flex-col items-center justify-center gap-y-2.5 rounded-md border border-border px-6 py-4 text-center text-xs">
          <h3 className="grid h-12 place-items-center text-4xl font-semibold lg:text-5xl">
            {defuses ? defuses : '-'}
          </h3>
          <p className="font-semibold uppercase text-muted-foreground">
            BOMB DEFUSES PER GAME
          </p>
          <CircleOff size={16} className="text-primary" />
        </div>
        <div className="flex h-48 flex-col items-center justify-center gap-y-2.5 rounded-md border border-border px-6 py-4 text-center text-xs">
          <h3 className="grid h-12 place-items-center text-4xl font-semibold lg:text-5xl">
            {utilityDamage ? utilityDamage : '-'}
          </h3>
          <p className="font-semibold uppercase text-muted-foreground">
            UTILITY DAMAGE PER ROUND
          </p>
          <Flame className="text-primary" />
        </div>
      </div>
    </div>
  );
};

export { TeamStats };
